import { useAsync, useMountEffect } from "@react-hookz/web";
import axios from "axios";
import { useSessionStorage } from "react-use";

import { config } from "shared/data";

export const useNavigatorInfo = () => {
  const [navigatorInfo, setNavigatorInfo] = useSessionStorage<NavigatorInfo>(
    "navigatorInfo",
    undefined,
  );

  const [asyncState, { execute }] = useAsync(async () => {
    if (navigatorInfo) return navigatorInfo;

    const response = await axios.get<NavigatorInfoResponse>(config.navigatorInfo.url, {
      headers: {
        "Access-Control-Allow-Origin": "*",
        apiKey: config.navigatorInfo.apiKey,
      },
    });
    if (!response.data) return;

    const parsed: NavigatorInfo = {
      botCategory: response.data["Bot-Category"] ?? null,
      botScore: response.data["Bot-Score"] ?? 0,
      botScoreGroup: getBotScoreGroup(response.data["Bot-Score"] ?? null),
      country: response.data["CF-IPCountry"] ?? "",
      ip: response.data["IP"] ?? "",
      isBot: response.data["IsVerifiedBot"] === true,
      isLightHouseCrawler: response.data["IsLightHouseCrawler"] === true,
    };
    setNavigatorInfo(parsed);

    return parsed;
  }, navigatorInfo);

  useMountEffect(() => {
    if (!navigatorInfo) void execute();
  });

  return {
    data: asyncState.result,
    loaded: asyncState.result !== undefined || ["error", "success"].includes(asyncState.status),
  };
};

const getBotScoreGroup = (botScore: null | number) => {
  if (!botScore) return "0";

  switch (true) {
    case botScore === 0:
      return "0";
    case botScore < 30:
      return "1-29";
    case botScore < 36:
      return "30-35";
    case botScore < 41:
      return "36-40";
    case botScore < 46:
      return "41-45";
    case botScore < 60:
      return "46-59";
    case botScore < 100:
      return "60-99";
    default:
      return "0";
  }
};

export type NavigatorInfo = {
  botCategory: string;
  botScore: number;
  botScoreGroup: "0" | "1-29" | "30-35" | "36-40" | "41-45" | "46-59" | "60-99";
  country: string;
  ip: string;
  isBot: boolean;
  isLightHouseCrawler: boolean;
};

export type NavigatorInfoResponse = {
  "Bot-Category": string;
  "Bot-Score": number;
  "CF-IPCountry": string;
  IP: string;
  IsLightHouseCrawler: boolean;
  IsVerifiedBot: boolean;
};
